import { isSameDay } from "date-fns";
import { useId, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useGetProfile } from "@/api/bale";
import { Badge } from "@/components/Badge";
import { Card, CardActionsHeader, CardTextContent } from "@/components/Card";
import { CreateEventModal } from "@/components/CreateEventModal";
import { DragHandle } from "@/components/drag-columns/components/DragItem";
import { Dropdown } from "@/components/Dropdown";
import { Icon } from "@/components/Icon";
import { IconButton } from "@/components/IconButton";
import { MenuItem } from "@/components/MenuItem";
import { Missing } from "@/components/Missing";
import { TextContent } from "@/components/TextContent";
import { Tomato } from "@/components/Tomato";
import { useCurrentTime } from "@/hooks/useCurrentTime";
import { useCurrentEventSuspense } from "@/pages/EventPage/hooks/useCurrentEvent";

import { PublicSharesModal } from ".//PublicSharesModal";
import { DeleteEventModal } from "./DeleteEventModal";

type Props = {
  onEdit: () => void;
};

export function StaticEventSummaryCard({ onEdit }: Props) {
  const { t } = useTranslation();
  const publicSharesModal = useRef<HTMLDialogElement>(null);
  const createEventModal = useRef<HTMLDialogElement>(null);
  const deleteEventModal = useRef<HTMLDialogElement>(null);
  const now = useCurrentTime("@minutely");
  const event = useCurrentEventSuspense({ query: { select: (data) => data.data } });
  const me = useGetProfile("@me", { query: { select: (data) => data.data.profile } });

  let whenText: ReactNode;
  if (!event.data.event.start_time) {
    whenText = (
      <Missing className="relative p-2">
        {t("event-time-not-set")}
        <Tomato className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2" />
      </Missing>
    );
  } else if (event.data.event.end_time) {
    whenText = isSameDay(event.data.event.start_time, event.data.event.end_time)
      ? t("time-range", { startDate: event.data.event.start_time, endDate: event.data.event.end_time })
      : t("datetime-range", { startDate: event.data.event.start_time, endDate: event.data.event.end_time });
  } else {
    whenText = t("datetime", { date: event.data.event.start_time });
  }

  const particpantMe = me.isSuccess
    ? event.data.event_participants.find(
        (participant) => participant.profile.id === me.data.id && participant.response === "Attending",
      )
    : undefined;

  const moreOptionsId = useId();

  return (
    <Card>
      <DragHandle>
        <CardActionsHeader title={event.data.event.name || t("untitled-event")}>
          {!!particpantMe?.limiting_discoverable_distance && (
            <IconButton
              type="button"
              name="share"
              aria-label={t("share-event")}
              onClick={() => publicSharesModal.current?.showModal()}
            />
          )}
          <IconButton type="button" name="bell" aria-label={t("notifications")} disabled />
          <IconButton type="button" name="more-vertical" aria-label={t("more-options")} popoverTarget={moreOptionsId} />
          <Dropdown
            id={moreOptionsId}
            popover="auto"
            className="absolute -mt-2 mr-2 min-w-[anchor-size(width)] [position-area:bottom_span-left]"
          >
            <MenuItem onClick={() => createEventModal.current?.showModal()}>
              <Icon name="copy" />
              {t("duplicate-event")}
            </MenuItem>
            {event.data.event.account_id === me.data?.id && (
              <MenuItem attention="danger" onClick={() => deleteEventModal.current?.showModal()}>
                <Icon name="trash" />
                {t("delete-event")}
              </MenuItem>
            )}
          </Dropdown>
          {me.isSuccess && event.data.event.account_id === me.data.id && (
            <IconButton type="button" name="edit" onClick={onEdit} aria-label={t("edit-event")} />
          )}
        </CardActionsHeader>
      </DragHandle>
      <CardTextContent className="flex flex-col gap-2">
        <div className="flex flex-row gap-2 empty:hidden">
          {event.data.event.end_time && event.data.event.end_time < now && <Badge>{t("event-has-ended")}</Badge>}
          {event.data.event.start_time &&
            event.data.event.start_time < now &&
            (!event.data.event.end_time || event.data.event.end_time > now) && <Badge>{t("happening-now")}</Badge>}
        </div>
        {!!event.data.event.description.raw && (
          <TextContent className="min-w-0" content={event.data.event.description} />
        )}
        <div className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-4 gap-y-2">
          <div className="font-hint">{t("event-when")}</div>
          <div className="font-interest">{whenText}</div>
          <div className="font-hint">{t("event-where")}</div>
          <div className="font-interest">
            {event.data.event.location ?? <Missing className="p-2">{t("event-location-not-set")}</Missing>}
          </div>
        </div>
      </CardTextContent>

      <PublicSharesModal ref={publicSharesModal} onClose={() => publicSharesModal.current?.close()} />
      <DeleteEventModal
        ref={deleteEventModal}
        event={event.data.event}
        onClose={() => deleteEventModal.current?.close()}
      />
      <CreateEventModal
        ref={createEventModal}
        template={event.data.event}
        onClose={() => createEventModal.current?.close()}
      />
    </Card>
  );
}
